import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Modal,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';
import config from '../config';

const server_URL = config.SERVER_URL;

const Customers = () => {
  const [openModal, setOpenModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingEncodeId, setTogglingEncodeId] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role_id: '',
  });
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    fetchCustomers();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ method: 'get_roles' }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setRoles(data.data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ method: 'list' }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      console.log('Received customer data:', data);
      
      if (data.status === '1' && data.data) {
        setUsers(Array.isArray(data.data) ? data.data : []);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role_id: '',
    });
    setSelectedLogo(null);
    setLogoPreview(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({
      username: '',
      email: '',
      password: '',
      role_id: '',
    });
    setSelectedLogo(null);
    setLogoPreview(null);
  };

  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role_id: user.role_id,
    });
    setSelectedLogo(null);
    setLogoPreview(user.logo_url || null);
    setEditModal(true);
  };

  const handleCloseEditModal = () => {
    setEditModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      role_id: '',
    });
    setSelectedLogo(null);
    setLogoPreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedLogo(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('method', 'add');
      formDataToSend.append('username', formData.username);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('password', formData.password);
      
      // Add logo file if selected
      if (selectedLogo) {
        formDataToSend.append('logo', selectedLogo);
      }

      const response = await fetch(`${server_URL}/api/customers`, {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();
      
      if (data.status === '1') {
        await fetchCustomers();
        handleCloseModal();
        alert('Customer added successfully');
      } else {
        alert(data.error || 'Failed to add customer');
      }
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('An error occurred while adding customer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('method', 'update');
      formDataToSend.append('id', editingUser.id);
      formDataToSend.append('username', formData.username);
      formDataToSend.append('email', formData.email);
      
      // Only include password if it's provided
      if (formData.password) {
        formDataToSend.append('password', formData.password);
      }
      
      // Add logo file if selected
      if (selectedLogo) {
        formDataToSend.append('logo', selectedLogo);
      }

      const response = await fetch(`${server_URL}/api/customers`, {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();
      
      if (data.status === '1') {
        await fetchCustomers();
        handleCloseEditModal();
        alert('Customer updated successfully');
      } else {
        alert(data.error || 'Failed to update customer');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      alert('An error occurred while updating customer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleEnable = async (id, enabled) => {
    try {
      const response = await fetch(`${server_URL}/api/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'toggle_enable',
          id: id,
          enabled: enabled,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update customer status');
      }

      const data = await response.json();
      if (data.status === '1') {
        await fetchCustomers();
      } else {
        alert(data.error || 'Failed to update customer status');
        // Refresh to revert the switch
        await fetchCustomers();
      }
    } catch (error) {
      console.error('Error toggling customer status:', error);
      alert('Failed to update customer status');
      // Refresh to revert the switch
      await fetchCustomers();
    }
  };

  const handleToggleIsEncode = async (id, isEncode) => {
    setTogglingEncodeId(id);
    try {
      const response = await fetch(`${server_URL}/api/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'toggle_is_encode',
          id: id,
          is_encode: isEncode,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update encoding status');
      }

      const data = await response.json();
      if (data.status === '1') {
        await fetchCustomers();
      } else {
        alert(data.error || 'Failed to update encoding status');
        // Refresh to revert the switch
        await fetchCustomers();
      }
    } catch (error) {
      console.error('Error toggling encoding status:', error);
      alert('Failed to update encoding status');
      // Refresh to revert the switch
      await fetchCustomers();
    } finally {
      setTogglingEncodeId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    setDeletingId(id);
    try {
      const response = await fetch(`${server_URL}/api/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'delete',
          id: id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete customer');
      }

      const data = await response.json();
      if (data.status === '1') {
        await fetchCustomers();
        alert('Customer deleted successfully');
      } else {
        alert(data.error || 'Failed to delete customer');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Failed to delete customer');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Customers
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenModal}
        >
          Add Customer
        </Button>
      </Box>
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <Typography sx={{ mt: 2 }}>Loading customers...</Typography>
          </Box>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Logo</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Encode</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography>No customers found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, index) => (
                  <TableRow 
                    key={user.id}
                    hover
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {user.logo_url ? (
                        <Box
                          component="img"
                          src={user.logo_url}
                          alt={user.username}
                          sx={{
                            width: 50,
                            height: 50,
                            objectFit: 'contain',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            color: '#999',
                            fontSize: '0.75rem',
                          }}
                        >
                          No Logo
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role_name}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={user.enabled === 1}
                            onChange={(e) => handleToggleEnable(user.id, e.target.checked)}
                            color="primary"
                          />
                        }
                        label={user.enabled === 1 ? 'Enabled' : 'Disabled'}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={user.is_encode === 1}
                            onChange={(e) => handleToggleIsEncode(user.id, e.target.checked)}
                            color="secondary"
                            disabled={togglingEncodeId === user.id}
                          />
                        }
                        label={user.is_encode === 1 ? 'Encode' : 'No Encode'}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleOpenEditModal(user)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(user.id)}
                        disabled={deletingId === user.id}
                      >
                        {deletingId === user.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Deleting...
                          </>
                        ) : (
                          'Delete'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Customer Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-customer-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Typography id="add-customer-modal-title" variant="h6" component="h2" mb={3}>
            Add Customer
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                label="Full Name"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload-add"
                  type="file"
                  onChange={handleLogoChange}
                />
                <label htmlFor="logo-upload-add">
                  <Button variant="outlined" component="span" fullWidth>
                    {selectedLogo ? selectedLogo.name : 'Upload Customer Logo'}
                  </Button>
                </label>
                {logoPreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Logo preview"
                      sx={{
                        maxWidth: 150,
                        maxHeight: 150,
                        objectFit: 'contain',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 1,
                      }}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Adding...
                    </>
                  ) : (
                    'Add Customer'
                  )}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Modal>

      {/* Edit Customer Modal */}
      <Modal
        open={editModal}
        onClose={handleCloseEditModal}
        aria-labelledby="edit-customer-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Typography id="edit-customer-modal-title" variant="h6" component="h2" mb={3}>
            Edit Customer
          </Typography>
          <form onSubmit={handleUpdate}>
            <Stack spacing={3}>
              <TextField
                label="Full Name"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                label="Password (leave blank to keep current)"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                helperText="Leave blank to keep current password"
              />
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload-edit"
                  type="file"
                  onChange={handleLogoChange}
                />
                <label htmlFor="logo-upload-edit">
                  <Button variant="outlined" component="span" fullWidth>
                    {selectedLogo ? selectedLogo.name : 'Upload Customer Logo'}
                  </Button>
                </label>
                {logoPreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Logo preview"
                      sx={{
                        maxWidth: 150,
                        maxHeight: 150,
                        objectFit: 'contain',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 1,
                      }}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseEditModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    'Update Customer'
                  )}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Modal>
    </Box>
  );
};

export default Customers;
